import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { useLocalProfile } from '@/hooks/useLocalProfile';
import { useEntitlements } from '@/hooks/useEntitlements';
import { loginAsGuest, redeemAccessCode, updateDisplayName } from '@/store/slices/authSlice';
import { FeatureFlagsSection } from '@/components/FeatureFlagsSection';
import { ScreensSection } from '@/components/ScreensSection';

type NameStatus = 'idle' | 'saving' | 'saved' | 'error';
type CodeStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * The Account tab — consolidates what used to be the "Access" (Profile) page:
 * display name, access-code redemption, plan summary, the recruiter dashboard
 * (code / stats / earnings), and feature flags. Linked from the desktop nav and
 * the mobile drawer.
 */
export function AccountPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, access } = useAuth();
  const localProfile = useLocalProfile();
  const { kind, ownedTier, tier3Active, dailyMedia, isRecruiter } = useEntitlements();

  const isGuest = !user || user.guest === true;
  const initialName = isGuest
    ? localProfile.displayName ?? user?.displayName ?? ''
    : user?.displayName ?? '';

  const [name, setName] = useState(initialName);
  const [nameStatus, setNameStatus] = useState<NameStatus>('idle');
  const [nameError, setNameError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [codeStatus, setCodeStatus] = useState<CodeStatus>('idle');
  const [codeError, setCodeError] = useState<string | null>(null);

  const needAccessBanner = Boolean((location.state as { needAccess?: boolean } | null)?.needAccess);

  useEffect(() => {
    if (nameStatus === 'idle') setName(initialName);
  }, [initialName, nameStatus]);

  // Scroll to the Screens section when arriving via the modal's #screens hash.
  useEffect(() => {
    if (location.hash === '#screens') {
      document.getElementById('screens')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  async function handleNameSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setNameStatus('error');
      setNameError(t('account.errors.nameEmpty'));
      return;
    }
    setNameStatus('saving');
    setNameError(null);

    if (isGuest) {
      localProfile.setDisplayName(trimmed);
      const result = await dispatch(loginAsGuest({ localId: localProfile.localId, displayName: trimmed }));
      if (loginAsGuest.fulfilled.match(result)) setNameStatus('saved');
      else {
        setNameStatus('error');
        setNameError((result.payload as string | undefined) ?? t('account.errors.nameUpdateFailed'));
      }
      return;
    }

    const result = await dispatch(updateDisplayName(trimmed));
    if (updateDisplayName.fulfilled.match(result)) setNameStatus('saved');
    else {
      setNameStatus('error');
      setNameError((result.payload as string | undefined) ?? t('account.errors.nameUpdateFailed'));
    }
  }

  async function handleCodeSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length === 0) return;
    setCodeStatus('submitting');
    setCodeError(null);

    const result = await dispatch(redeemAccessCode(trimmed));
    if (redeemAccessCode.fulfilled.match(result)) {
      setCodeStatus('success');
      setCode('');
    } else {
      setCodeStatus('error');
      setCodeError((result.payload as string | undefined) ?? t('account.errors.invalidCode'));
    }
  }

  const comped = kind === 'comp' || kind === 'admin';
  const ownedLabel = ownedTier === 'tier2' ? t('account.plan.ownedTier2')
    : ownedTier === 'tier1' ? t('account.plan.ownedTier1') : null;

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('account.title')}</h1>
        <p className="muted">{isGuest ? t('account.guest') : t('account.signedInAs', { email: user!.email })}</p>
      </header>

      {needAccessBanner && !access && (
        <div className="error" role="alert">
          {t('account.needAccess')}
        </div>
      )}

      <section className="admin-card">
        <h2 className="admin-card__title">{t('account.profile')}</h2>
        <form onSubmit={handleNameSubmit} noValidate>
          <div className="field">
            <label htmlFor="account-name">{t('account.displayName')}</label>
            <input
              id="account-name"
              type="text"
              autoComplete="nickname"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameStatus !== 'idle') setNameStatus('idle');
              }}
              maxLength={80}
            />
          </div>
          <button type="submit" className="btn" disabled={nameStatus === 'saving'}>
            {nameStatus === 'saving' ? t('common.saving') : t('account.saveName')}
          </button>
          {nameStatus === 'saved' && <p className="muted" role="status" style={{ marginTop: 12 }}>{t('account.nameUpdated')}</p>}
          {nameStatus === 'error' && nameError && <p className="error" role="alert" style={{ marginTop: 12 }}>{nameError}</p>}
        </form>
      </section>

      <section className="admin-card">
        <h2 className="admin-card__title">{t('account.accessCode')}</h2>
        {access ? (
          <p className="muted" role="status">{t('account.accessGranted')}</p>
        ) : (
          <form onSubmit={handleCodeSubmit} noValidate>
            <div className="field">
              <label htmlFor="account-code">{t('account.enterCode')}</label>
              <input
                id="account-code"
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (codeStatus !== 'idle') setCodeStatus('idle');
                }}
                autoCapitalize="characters"
                spellCheck={false}
              />
            </div>
            <button type="submit" className="btn" disabled={codeStatus === 'submitting' || code.trim().length === 0}>
              {codeStatus === 'submitting' ? t('account.testing') : t('account.testCode')}
            </button>
            {codeStatus === 'success' && <p className="muted" role="status" style={{ marginTop: 12 }}>{t('account.accessGrantedLower')}</p>}
            {codeStatus === 'error' && <p className="error" role="alert" style={{ marginTop: 12 }}>{codeError ?? t('account.errors.invalidCode')}</p>}
          </form>
        )}
      </section>

      {!isGuest && (
        <section className="admin-card">
          <h2 className="admin-card__title">{t('account.plan.title')}</h2>
          {comped ? (
            <p className="muted">{t('account.plan.comped')}</p>
          ) : (
            <>
              {ownedLabel ? (
                <p>
                  <Trans
                    i18nKey="account.plan.owned"
                    values={{ label: ownedLabel }}
                    components={{ strong: <strong />, muted: <span className="muted" /> }}
                  />
                </p>
              ) : dailyMedia ? (
                <p className="muted">
                  {t('account.plan.freePlays', { remaining: dailyMedia.remaining, limit: dailyMedia.limit })}
                  {dailyMedia.adRequired ? t('account.plan.adSuffix') : ''}
                </p>
              ) : (
                <p className="muted">{t('account.plan.noPlan')}</p>
              )}
              {tier3Active && <p className="muted">{t('account.plan.premiumActive')}</p>}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                <Link to="/subscribe" className="btn">{ownedTier ? t('account.plan.upgrade') : t('account.plan.choosePlan')}</Link>
                <Link to="/account/billing" className="btn-ghost">{t('account.plan.billingDetails')}</Link>
              </div>
            </>
          )}
        </section>
      )}

      {!isGuest && <ScreensSection />}

      {!isGuest && isRecruiter && (
        <section className="admin-card" data-testid="recruiter-tools-link">
          <h2 className="admin-card__title">{t('recruitment.account.linkTitle')}</h2>
          <p className="muted">{t('recruitment.account.linkBody')}</p>
          <Link to="/recruitment/earnings" className="btn">{t('recruitment.account.linkCta')}</Link>
        </section>
      )}

      {!isGuest && (
        <section className="admin-card">
          <FeatureFlagsSection />
        </section>
      )}
    </main>
  );
}
